import React, { useState, useEffect, useRef } from "react";
import AdminSidebar from "../components/AdminSidebar";
import { io } from "socket.io-client";

const socket = io("https://api.confidanto.com");

const AdminDashboard = ({ loggedInUser }) => {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [unreadMessages, setUnreadMessages] = useState({});
  const [messageStatus, setMessageStatus] = useState(null);

  const selectedEmailRef = useRef(selectedEmail);

  useEffect(() => {
    selectedEmailRef.current = selectedEmail;
  }, [selectedEmail]);

  useEffect(() => {
    fetch("https://api.confidanto.com/chatbox-api/api/emails")
      .then((response) => response.json())
      .then((data) => {
        setEmails(data);
      })
      .catch((error) => console.error("Error fetching initial emails:", error));
    socket.on("newUserMessage", (newMessage) => {
      setEmails((prevEmails) => {
        if (!prevEmails.includes(newMessage.email)) {
          return [newMessage.email, ...prevEmails];
        }
        return prevEmails;
      });

      if (newMessage.email === selectedEmailRef.current) {
        setMessages((prevMessages) => [...prevMessages, newMessage]);
      } else {
        setUnreadMessages((prev) => ({ ...prev, [newMessage.email]: true }));
      }
    });

    return () => {
      socket.off("newUserMessage");
    };
  }, []);

  useEffect(() => {
    if (selectedEmail) {
      fetch(
        `https://api.confidanto.com/chatbox-api/api/messages/${selectedEmail}`
      )
        .then((response) => response.json())
        .then((data) => {
          setMessages(data);
          setUnreadMessages((prev) => ({ ...prev, [selectedEmail]: false }));
        })
        .catch((error) => console.error("Error fetching messages:", error));
    } else {
      setMessages([]);
    }
  }, [selectedEmail]);

  const handleSendMessage = () => {
    if (inputValue.trim() && selectedEmail) {
      const newMessage = {
        email: selectedEmail,
        name: localStorage.getItem("current_admin"),
        text: inputValue,
        from: "admin",
        timestamp: new Date().toISOString(),
      };

      fetch("https://api.confidanto.com/chatbox-api/api/admin/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newMessage),
      })
        .then((response) => response.json())
        .then(() => {
          setMessages((prevMessages) => [...prevMessages, newMessage]);
          setInputValue("");
          setTimeout(() => setMessageStatus(null), 3000);
        })
        .catch((error) => {
          console.error("Error saving admin message:", error);
          setMessageStatus("Failed to send message.");
          setTimeout(() => setMessageStatus(null), 3000);
        });
    }
  };

  // const currentAdmin = localStorage.getItem("current_admin") ?? "Admin";

  return (
    <div className="flex h-screen">
      <AdminSidebar />
      <div className="w-1/4 bg-gray-100 border-r border-gray-200">
        <h2 className="text-lg font-bold p-4">Chats</h2>
        <ul>
          {emails.map((email, index) => (
            <li
              key={index}
              className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-200 ${
                email === selectedEmail ? "bg-gray-200" : ""
              }`}
              onClick={() => setSelectedEmail(email)}
            >
              <div className="flex justify-between items-center">
                <p className="font-semibold">{email}</p>
                {unreadMessages[email] && (
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-1 bg-white p-6">
        <div className="bg-white p-4 rounded-lg shadow-md h-full flex flex-col">
          {messageStatus && (
            <div
              className={`p-2 mb-4 text-white rounded-md ${
                messageStatus.includes("success")
                  ? "bg-green-500"
                  : "bg-red-500"
              }`}
            >
              {messageStatus}
            </div>
          )}

          <div className="flex-grow overflow-y-auto space-y-4 p-4">
            {messages.length === 0 ? (
              <p className="text-center text-gray-500">
                {selectedEmail
                  ? "No messages found for this email"
                  : "Select a chat to view messages"}
              </p>
            ) : (
              messages.map((message, index) => {
                const isAdmin = message.from === "admin";
                return (
                  <div
                    key={index}
                    className={`flex ${
                      isAdmin ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div className="flex flex-col">
                      {isAdmin && (
                        <p className="text-sm text-gray-500 mb-1 text-right px-1">
                          {message.name}
                        </p>
                      )}
                      <div
                        className={`max-w-lg lg:max-w-xl px-4 py-3 rounded-lg ${
                          isAdmin
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-900"
                        }`}
                      >
                        <p className="break-words">{message.text}</p>
                        <span
                          className={`block text-xs text-right mt-2 ${
                            isAdmin ? "text-blue-100" : "text-gray-500"
                          }`}
                        >
                          {new Date(message.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="border-t border-gray-200 p-4 flex items-center">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              className="flex-grow p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSendMessage}
              className="ml-2 bg-gray-500 text-white p-2 rounded-md hover:bg-gray-600 focus:outline-none"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
